import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { dashApi } from "@/lib/dashApi";
import { fmtRelative } from "@/lib/format";
import { EmptyState, ErrorState, LoadingState, PageHeader, Panel, StatusPill, TrustPill } from "@/components/dash/ui";

function LabelCounts({ counts }: { counts: Record<string, number> }) {
  const entries = Object.entries(counts);
  if (!entries.length) return <span className="text-sm text-slate-500">No data</span>;
  return (
    <div className="flex flex-wrap gap-2">
      {entries.map(([label, n]) => (
        <span key={label} className="inline-flex items-center gap-1.5"><TrustPill label={label} /><span className="text-xs text-slate-400">{n}</span></span>
      ))}
    </div>
  );
}

export default function TrustOverviewPage() {
  const q = useQuery({ queryKey: ["trust-overview"], queryFn: dashApi.trustOverview });
  const d = q.data;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Data Trust Overview"
        description="Can we trust this number? A deterministic view of which sources, KPIs and figures are trusted, partial, estimated, stale, conflicting or insufficient. Read-only — trust is never fabricated."
      />
      {q.isLoading ? <LoadingState /> : q.isError ? <ErrorState error={q.error} /> : d && (
        <>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Panel title="Connector trust" right={<Link to="/trust/connectors" className="text-xs text-brand-300 hover:text-brand-200">Detail →</Link>}>
              <LabelCounts counts={d.connectors} />
            </Panel>
            <Panel title="KPI trust" right={<Link to="/trust/kpis" className="text-xs text-brand-300 hover:text-brand-200">Detail →</Link>}>
              <LabelCounts counts={d.kpis} />
            </Panel>
            <Panel title="Data-quality issues">
              <div className="flex flex-wrap gap-2">
                {(["critical", "warning", "info"] as const).map((s) => d.data_quality[s] ? <StatusPill key={s} status={s} label={`${d.data_quality[s]} ${s}`} /> : null)}
                {!Object.keys(d.data_quality).length && <span className="text-sm text-slate-500">None</span>}
              </div>
            </Panel>
            <Panel title="Reconciliation" right={<Link to="/trust/reconciliation" className="text-xs text-brand-300 hover:text-brand-200">Detail →</Link>}>
              <div className="text-sm text-slate-300">{d.reconciliation.total} checks</div>
              <div className="mt-1 text-xs text-slate-500">
                {d.reconciliation.counts.conflict ? `${d.reconciliation.counts.conflict} conflict(s) · ` : ""}
                {d.reconciliation.counts.double_counting_risks ?? 0} double-count risk(s)
              </div>
            </Panel>
          </div>

          <Panel title="Connector freshness">
            {!d.connector_rows.length ? <EmptyState compact>No connectors configured.</EmptyState> : (
              <table className="w-full text-sm">
                <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
                  <th className="pb-2 font-medium">Source</th><th className="pb-2 font-medium">Trust</th>
                  <th className="pb-2 font-medium">Reason</th><th className="pb-2 text-right font-medium">Last data</th>
                </tr></thead>
                <tbody className="divide-y divide-ink-500/40">
                  {d.connector_rows.map((c) => (
                    <tr key={c.source} className="text-slate-300">
                      <td className="py-2 text-slate-100">{c.name}</td>
                      <td className="py-2"><TrustPill label={c.label} /></td>
                      <td className="py-2 text-xs text-slate-400">{c.reason}</td>
                      <td className="py-2 text-right text-[11px] text-slate-500">{c.freshness ? fmtRelative(c.freshness) : "never"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>

          <Panel title={`Data-quality issues (${d.issue_rows.length})`}>
            {!d.issue_rows.length ? <EmptyState compact>No data-quality issues detected.</EmptyState> : (
              <ul className="space-y-2">
                {d.issue_rows.map((i, idx) => (
                  <li key={idx} className="flex items-start gap-2.5">
                    <StatusPill status={i.severity} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-slate-200">{i.source ?? "—"} · <TrustPill label={i.label} /></p>
                      <p className="text-xs text-slate-500">{i.reason}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </>
      )}
    </div>
  );
}
